export const RESULT_DISCIPLINES = [
  { code: 'Cani-VTT', label: 'Cani - VTT', labelKey: 'raceDetail.vtt' },
  {
    code: 'Cani-Trottinette',
    label: 'Cani - Trottinette',
    labelKey: 'raceDetail.trottinette',
  },
  { code: 'CanicrossLong', label: 'Canicross - Long', labelKey: 'raceDetail.long' },
  { code: 'Cani-Trail', label: 'Cani Trail', labelKey: 'raceDetail.trail' },
  { code: 'Cani-Marche', label: 'Cani-Marche', labelKey: 'raceDetail.marche' },
  { code: 'CanicrossCourt', label: 'Canicross - Court', labelKey: 'raceDetail.court' },
  { code: 'CanicrossCadet', label: 'Canicross - Cadet', labelKey: 'raceDetail.cadet' },
  { code: 'CanicrossMinime', label: 'Canicross - Minimes', labelKey: 'raceDetail.minime' },
  {
    code: 'CanicrossBenjamin',
    label: 'Canicross - Benjamin',
    labelKey: 'raceDetail.benjamin',
  },
] as const;

export type ResultDiscipline = (typeof RESULT_DISCIPLINES)[number]['code'];

const DISCIPLINE_BY_CODE = new Map<ResultDiscipline, (typeof RESULT_DISCIPLINES)[number]>(
  RESULT_DISCIPLINES.map(discipline => [discipline.code, discipline]),
);

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function disciplineFromCategory(category: string): ResultDiscipline | null {
  const normalized = category.toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (!normalized) return null;

  if (normalized.startsWith('CP')) return 'Cani-Trottinette';
  if (normalized.startsWith('CM')) return 'Cani-Marche';
  if (normalized.startsWith('V') && !/^V[123]/.test(normalized)) return 'Cani-VTT';

  // FFSLC youth categories are generally CFB/CHB, CFM/CHM and CFC/CHC.
  const ageCode = normalized.match(/^C[FMH]?([BMC])$/)?.[1] ?? normalized.match(/^C[FMH]([BMC])/i)?.[1];
  if (ageCode === 'B') return 'CanicrossBenjamin';
  if (ageCode === 'M') return 'CanicrossMinime';
  if (ageCode === 'C') return 'CanicrossCadet';

  return null;
}

/**
 * Maps importer aliases and historical race names to the disciplines shown
 * in event results. Generic "Canicross" data is treated as the long course unless
 * the race name or category identifies a more specific discipline.
 */
export function normalizeResultDiscipline(
  discipline?: string | null,
  raceName?: string | null,
  category?: string | null,
): ResultDiscipline | null {
  const rawDiscipline = discipline?.trim() ?? '';
  if ((!rawDiscipline || /^(canicross|canicrosslong|canitrail|cani[ -]trail)$/i.test(rawDiscipline))
    && /cani\s*trail/.test(normalize(`${rawDiscipline} ${raceName ?? ''}`))) return 'Cani-Trail';
  if (DISCIPLINE_BY_CODE.has(rawDiscipline as ResultDiscipline)) {
    return rawDiscipline as ResultDiscipline;
  }

  const value = normalize(`${rawDiscipline} ${raceName ?? ''}`);
  if (/\b(vtt|velo|bike)\b|canivtt/.test(value)) return 'Cani-VTT';
  if (/trott|pedicycle|scooter|patinette/.test(value)) return 'Cani-Trottinette';
  if (/marche|randonnee|\brando\b|\bwalk\b/.test(value)) return 'Cani-Marche';
  if (/benjamin|\bu13\b/.test(value)) return 'CanicrossBenjamin';
  if (/minime|\bu15\b/.test(value)) return 'CanicrossMinime';
  if (/cadet|\bu17\b/.test(value)) return 'CanicrossCadet';
  if (/court|sprint|canicourt|decouverte/.test(value)) return 'CanicrossCourt';
  if (/^CT[HF]/i.test(category ?? '') && !raceName?.trim()) return 'Cani-Trail';
  if (/long|canicross|cani cross/.test(value)) {
    return disciplineFromCategory(category ?? '') ?? 'CanicrossLong';
  }

  return disciplineFromCategory(category ?? '');
}

export function getResultDisciplineDefinition(code: ResultDiscipline) {
  return DISCIPLINE_BY_CODE.get(code)!;
}

export function getResultDisciplineOrder(code: ResultDiscipline | null): number {
  if (!code) return RESULT_DISCIPLINES.length;
  return RESULT_DISCIPLINES.findIndex(discipline => discipline.code === code);
}
